import { InvalidUrlError, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import InviteParticipantStore from '..';
import { AppyError, callApi } from '../../../../../../../../../../../../Appy';
import { currentUserStore } from '../../../../../../../../../../../../CurrentUserStore';
import ErrorActionableReaction, {
  EAProps,
} from '../../../../../../../../../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../../../../../../../../../LinkyLinky';
import { participantResourceDecoder } from '../../../../../../../../../../../../ParticipantsStore/Decoders';
import { FormTemplate } from '../../../../../../../../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../../../../../../../../Resource/Types';
import { groupParticipantsResourceDecoder } from '../../../../../../../../../../../People/Group/GroupParticipantsStore/Decoders';
import {
  allSelectedParticipantIds,
  buildLicenseInformationUrl,
  filterGroups,
  filterParticipants,
} from '../../../../../../../../../Functions';
import { LearningCollectionPublishResource } from '../../../../../Store/Types';
import {
  participantsInvitedResourceDecoder,
  productLicenseInformationResourceDecoder,
} from '../Decoder';
import { State, Submitting } from '../Types';
import { learningCollectionInvitationsEncoder } from './Encoders';
import { handleInviteError } from './ErrorHandling';

interface Props extends EAProps<InviteParticipantStore> {
  learningCollectionPublishResource: LearningCollectionPublishResource;
  template: FormTemplate;
}

type InviteError = AppyError | InvalidUrlError | MissingLinkError;

const sendParticipantRequest = callApi(participantResourceDecoder, {});

const sendGroupRequest = callApi(groupParticipantsResourceDecoder, {});

const buildUrl = (link: Link): Task<InviteError, Link> =>
  toUrlT(link.href).map((url): Link => ({ ...link, href: url.href }));

const getParticipant = (link: Link) => buildUrl(link).andThen(sendParticipantRequest);

const getGroup = (link: Link) => buildUrl(link).andThen(sendGroupRequest);

const processLink =
  (id: string, replace: string) =>
  (link: Link): Task<InviteError, Link> =>
    toUrlT(link.href.replace(`{${replace}}`, `${id}`)).map(
      (url): Link => ({ ...link, href: url.href }),
    );

const newParticipant = (store: InviteParticipantStore, participantId: string) =>
  Task.succeed<InviteError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('participant'))
    .andThen(processLink(participantId, 'participant_id'))
    .andThen(getParticipant)
    .fork(handleInviteError(store), store.getLicenseInformationWithNewParticipant);

const newGroup = (store: InviteParticipantStore, groupId: string) =>
  Task.succeed<InviteError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('user-group'))
    .andThen(processLink(groupId, 'group_id'))
    .andThen(getGroup)
    .fork(handleInviteError(store), store.getLicenseInformationWithNewGroup);

const submit = (
  store: InviteParticipantStore,
  state: Submitting,
  learningCollectionPublishResource: LearningCollectionPublishResource,
  template: FormTemplate,
) =>
  Task.succeed<InviteError, ReadonlyArray<Link>>(learningCollectionPublishResource.links)
    .andThen(findLinkT('invite'))
    .andThen(
      callApi(
        participantsInvitedResourceDecoder,
        learningCollectionInvitationsEncoder(
          state.timeZone,
          template,
          learningCollectionPublishResource,
          filterParticipants(state.newGroupsAndParticipants).map((p) => p.payload.id),
          filterGroups(state.newGroupsAndParticipants).map((p) => p.payload.id),
          state.skipCommunications,
        ),
      ),
    )
    .fork(handleInviteError(store), store.participantsInvited);

class InviteParticipantReactions extends ErrorActionableReaction<
  InviteParticipantStore,
  State,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store, learningCollectionPublishResource, template } = this.props;
    switch (state.kind) {
      case 'new-participant':
        newParticipant(store, state.participantId);
        break;
      case 'new-group':
        newGroup(store, state.groupId);
        break;
      case 'remove-participant':
        store.licenseInformationWithRemovedParticipant();
        break;
      case 'remove-group':
        store.licenseInformationWithRemovedGroup();
        break;
      case 'submitting':
        submit(store, state, learningCollectionPublishResource, template);
        break;
      case 'get-license-information':
        Task.succeed<InviteError, ReadonlyArray<Link>>(learningCollectionPublishResource.links)
          .andThen(findLinkT('product-licenses'))
          .andThen(
            buildLicenseInformationUrl(allSelectedParticipantIds(state.newGroupsAndParticipants)),
          )
          .andThen(callApi(productLicenseInformationResourceDecoder, {}))
          .fork(handleInviteError(store), store.readyWithLicenseInformation);
        break;
      case 'ready':
      case 'inviting':
      case 'participants-invited':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default InviteParticipantReactions;
